import { SEOLanguage } from "@/data/seoTranslations";

export interface JournalMeta {
  title: string;
  description: string;
  date: string; // YYYY-MM-DD
  image: string;
  author: string;
  category: string;
  keywords?: string[];
  faqs?: {
    question: string;
    answer: string;
  }[];
}

export interface JournalPost {
  slug: string;
  language: SEOLanguage;
  meta: JournalMeta;
  component: React.ComponentType;
}

// Helper to extract language from filename: slug.lang.mdx
const parseFileName = (path: string) => {
  const parts = path.split("/");
  const fileName = parts[parts.length - 1];
  const [slug, lang] = fileName.split(".");
  return { slug, lang: lang as SEOLanguage };
};

export const getAllPosts = async (): Promise<JournalPost[]> => {
  // Import all MDX files from content/journal
  const modules = import.meta.glob("/src/content/journal/*.mdx", { eager: true });
  
  const posts: JournalPost[] = [];

  for (const path in modules) {
    const { slug, lang } = parseFileName(path);
    const mod = modules[path] as any;
    
    if (mod && mod.meta) {
      posts.push({
        slug,
        language: lang,
        meta: mod.meta,
        component: mod.default,
      });
    }
  }

  // Sort by date descending
  return posts.sort((a, b) => 
    new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime()
  );
};

export const getPostsByLanguage = async (language: SEOLanguage): Promise<JournalPost[]> => {
  const allPosts = await getAllPosts();
  return allPosts.filter(post => post.language === language);
};

export const getPostBySlug = async (slug: string, language: SEOLanguage): Promise<JournalPost | undefined> => {
  const allPosts = await getAllPosts();
  return allPosts.find(post => post.slug === slug && post.language === language);
};
